export type TaProposalCapability = 'supported' | 'blocked';

export type TaProposalPhase =
  | 'blocked'
  | 'unavailable'
  | 'awaiting_approval'
  | 'applying'
  | 'applied'
  | 'failed'
  | 'declined'
  | 'pending';

export interface TaProposalParagraph {
  id: string;
  location: string | null;
  before: string;
  after: string;
}

export interface TaProposalTarget {
  issueId: string;
  runId: string;
  tenantId: string;
  pageUrl: string;
  wordpressId: number | null;
  contentType: string | null;
}

export interface TechnicalAuditProposal {
  proposalId: string;
  issueType: string;
  target: TaProposalTarget;
  paragraphs: TaProposalParagraph[];
  capability: TaProposalCapability;
  blockedReason: string | null;
  approvalRequestId: string | null;
  expiresAt: string | null;
}

export interface TaProposalSkip {
  id: string;
  reason: string;
}

export interface TaProposalReceipt {
  proposalId: string;
  status: 'applied' | 'failed';
  appliedParagraphIds: string[];
  skipped: TaProposalSkip[];
  settledIssueId: string | null;
  settledRunId: string | null;
  message: string | null;
}

export interface TaApprovalDecision {
  proposalId: string;
  approvalRequestId: string;
  decision: 'approve' | 'decline';
}

export type TaApprovalOutcome =
  | { ok: true; receipt: TaProposalReceipt }
  | { ok: true; pending: true }
  | { ok: false; reason: string };

const str = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

const optionalStr = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

const parseParagraph = (raw: unknown): TaProposalParagraph | null => {
  if (!raw || typeof raw !== 'object') return null;
  const source = raw as Record<string, unknown>;
  const id = str(source.id);
  if (!id) return null;
  const before = typeof source.before === 'string' ? source.before : null;
  const after = typeof source.after === 'string' ? source.after : null;
  if (before === null || after === null) return null;
  return { id, location: optionalStr(source.location), before, after };
};

const parseTarget = (raw: unknown): TaProposalTarget | null => {
  if (!raw || typeof raw !== 'object') return null;
  const source = raw as Record<string, unknown>;
  const issueId = str(source.issue_id ?? source.issueId);
  const runId = str(source.run_id ?? source.runId);
  const tenantId = str(source.tenant_id ?? source.tenantId);
  const pageUrl = str(source.page_url ?? source.pageUrl);
  if (!issueId || !runId || !tenantId || !pageUrl) return null;
  const rawWordpressId = source.wordpress_id ?? source.wordpressId;
  const wordpressId =
    typeof rawWordpressId === 'number' && Number.isFinite(rawWordpressId)
      ? rawWordpressId
      : null;
  return {
    issueId,
    runId,
    tenantId,
    pageUrl,
    wordpressId,
    contentType: optionalStr(source.content_type ?? source.contentType),
  };
};

export const parseTaProposal = (raw: unknown): TechnicalAuditProposal | null => {
  if (!raw || typeof raw !== 'object') return null;
  const source = raw as Record<string, unknown>;
  const proposalId = str(source.proposal_id ?? source.proposalId);
  const issueType = str(source.issue_type ?? source.issueType);
  if (!proposalId || !issueType) return null;

  const target = parseTarget(source.target);
  if (!target) return null;

  const rawParagraphs = source.paragraphs;
  if (!Array.isArray(rawParagraphs) || rawParagraphs.length === 0) return null;
  const paragraphs: TaProposalParagraph[] = [];
  for (const entry of rawParagraphs) {
    const parsed = parseParagraph(entry);
    if (!parsed) return null;
    paragraphs.push(parsed);
  }

  const rawCapability = source.capability;
  const capability: TaProposalCapability =
    rawCapability === 'supported' ? 'supported' : 'blocked';

  return {
    proposalId,
    issueType,
    target,
    paragraphs,
    capability,
    blockedReason: optionalStr(source.blocked_reason ?? source.blockedReason),
    approvalRequestId: str(source.approval_request_id ?? source.approvalRequestId),
    expiresAt: optionalStr(source.expires_at ?? source.expiresAt),
  };
};

export const parseTaProposalReceipt = (
  raw: unknown,
  proposalId: string,
): TaProposalReceipt | null => {
  if (!raw || typeof raw !== 'object') return null;
  const source = raw as Record<string, unknown>;
  const receiptProposalId = str(source.proposal_id ?? source.proposalId);
  if (!receiptProposalId || receiptProposalId !== proposalId) return null;
  const status = source.status === 'applied' ? 'applied' : source.status === 'failed' ? 'failed' : null;
  if (!status) return null;

  const appliedParagraphIds: string[] = [];
  const rawApplied = source.applied_paragraph_ids ?? source.appliedParagraphIds;
  if (Array.isArray(rawApplied)) {
    for (const entry of rawApplied) {
      const id = str(entry);
      if (id) appliedParagraphIds.push(id);
    }
  }

  const skipped: TaProposalSkip[] = [];
  const rawSkipped = source.skipped;
  if (Array.isArray(rawSkipped)) {
    for (const entry of rawSkipped) {
      if (!entry || typeof entry !== 'object') continue;
      const item = entry as Record<string, unknown>;
      const id = str(item.id);
      const reason = str(item.reason);
      if (id && reason) skipped.push({ id, reason });
    }
  }

  return {
    proposalId: receiptProposalId,
    status,
    appliedParagraphIds,
    skipped,
    settledIssueId: str(source.settled_issue_id ?? source.settledIssueId),
    settledRunId: str(source.settled_run_id ?? source.settledRunId),
    message: optionalStr(source.message),
  };
};

export interface RestoredTaProposal {
  phase: 'applied' | 'failed' | 'declined' | 'pending';
  receipt: TaProposalReceipt | null;
}

export const parseRestoredTaProposal = (
  raw: unknown,
  status: unknown,
): { proposal: TechnicalAuditProposal; restored: RestoredTaProposal | null } | null => {
  const proposal = parseTaProposal(raw);
  if (!proposal) return null;
  if (status === 'rejected') {
    return { proposal, restored: { phase: 'declined', receipt: null } };
  }
  if (status !== 'accepted') {
    return { proposal, restored: null };
  }
  const receipt = parseTaProposalReceipt(
    (raw as Record<string, unknown>).receipt,
    proposal.proposalId,
  );
  if (!receipt) {
    return { proposal, restored: { phase: 'pending', receipt: null } };
  }
  return { proposal, restored: { phase: receipt.status, receipt } };
};

export const restoredFromTaOutcome = (
  outcome: Extract<TaApprovalOutcome, { ok: true }>,
  decision: 'approve' | 'decline',
): RestoredTaProposal => {
  if ('receipt' in outcome && outcome.receipt) {
    if (outcome.receipt.status === 'applied') {
      return { phase: 'applied', receipt: outcome.receipt };
    }
    if (decision === 'decline') {
      return { phase: 'declined', receipt: null };
    }
    return { phase: 'failed', receipt: outcome.receipt };
  }
  return { phase: 'pending', receipt: null };
};
